/**
 * Adaptador do Widget do site (5o canal) para o shell compartilhado do inbox.
 *
 * O que so o widget tem (§3.4 do prompt de 22/09):
 * - sem janela de 24 h (sempre aberta) e entrega DIFERIDA: a bolha nasce
 *   `sent` e vira `delivered`/`read` quando o visitante a ve;
 * - N widgets por workspace: seletor de widget na lista (analogo do numero
 *   de origem do WhatsApp) e pagina de cards em /widget/conexoes;
 * - botoes/lista interativos renderizados na bolha. O contexto de pagina
 *   (`context.page_url`) NAO aparece no inbox (pedido do dono, 23/09: e
 *   informacao tecnica); vive no doc para o agente ({{pagina_atual}}) e para
 *   a origem do lead;
 * - so imagem como midia; sem gravacao de audio.
 */
import React from 'react';
import { Globe } from 'lucide-react';
import widgetService from '../../../services/widget/widgetService';
import { loadWidgetMessages } from '../../../services/inbox/chatMessageLoaders';

// Seletor de widget de origem (chips), como o seletor de numero do QR.
const WidgetSelector = ({ colors, account, value, onChange }) => {
  const widgets = account?.widgets || [];
  if (widgets.length <= 1) return null;
  const chip = (active) => ({
    padding: '4px 10px', borderRadius: 14, fontSize: 11, fontWeight: 600, cursor: 'pointer',
    border: `1px solid ${active ? colors.accentPrimary : colors.borderColor}`,
    backgroundColor: active ? `${colors.accentPrimary}1f` : 'transparent',
    color: active ? colors.accentPrimary : colors.textSecondary, whiteSpace: 'nowrap',
  });
  return (
    <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 8, alignItems: 'center' }}>
      <button type="button" style={chip(!value)} onClick={() => onChange?.(null)}>Todos os widgets</button>
      {widgets.map((w) => (
        <button key={w.id} type="button" style={chip(value === w.id)} onClick={() => onChange?.(w.id)} title={w.name}>
          {w.name}
        </button>
      ))}
    </div>
  );
};

// Botoes/lista persistidos na bolha (o widget e o 2o canal com botoes nativos).
const renderInteractive = (msg, { colors, fromMe }) => {
  const inter = msg?.interactive;
  if (!inter) return null;
  const items = inter.kind === 'list'
    ? [].concat(...(inter.sections || []).map((s) => s.rows || []))
    : (inter.buttons || []);
  if (!items.length) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
      {items.map((b) => (
        <span key={b.id || b.title} style={{
          padding: '3px 10px', borderRadius: 999, fontSize: 12,
          border: `1px solid ${fromMe ? 'rgba(255,255,255,0.45)' : colors.accentPrimary}`,
          color: fromMe ? 'inherit' : colors.accentPrimary, opacity: 0.9,
        }}>
          {b.title}
        </span>
      ))}
    </div>
  );
};

// Carrossel de produtos que o agente mostrou ao visitante (tools render_*;
// o widget e o unico canal que desenha, mapa de canais `cards`). No inbox vai
// uma vitrine compacta: foto, nome, preco e o link do produto.
const renderCards = (msg, { colors }) => {
  const list = Array.isArray(msg?.cards) ? msg.cards : (msg?.cards?.cards || []);
  if (!list.length) return null;
  return (
    <div style={{ display: 'flex', gap: 8, overflowX: 'auto', maxWidth: 360, paddingBottom: 2, marginTop: 2 }}>
      {list.map((c, i) => {
        const href = /^https?:\/\//i.test(String(c.action_url || '')) ? c.action_url : undefined;
        return (
          <a
            key={c.id || i}
            href={href}
            target="_blank"
            rel="noreferrer"
            style={{
              flex: '0 0 128px', display: 'flex', flexDirection: 'column', gap: 4, padding: 6, borderRadius: 8,
              textDecoration: 'none', color: 'inherit', border: `1px solid ${colors.borderColor}`,
              backgroundColor: colors.bgPrimary, cursor: href ? 'pointer' : 'default',
            }}
          >
            {c.image_url && (
              <img src={c.image_url} alt="" style={{ width: '100%', height: 76, objectFit: 'contain', borderRadius: 4, backgroundColor: '#fff' }} />
            )}
            <span style={{
              fontSize: 12, fontWeight: 600, lineHeight: 1.3, overflow: 'hidden',
              display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
            }}>
              {c.title || 'Produto'}
            </span>
            {c.price && <span style={{ fontSize: 12, opacity: 0.8 }}>{c.price}</span>}
          </a>
        );
      })}
    </div>
  );
};

const renderBubbleExtra = (msg, ctx) => (
  <>
    {renderInteractive(msg, ctx)}
    {renderCards(msg, ctx)}
  </>
);

const widgetAdapter = {
  code: 'widget',
  label: 'Widget do site',
  prefix: 'widget:',
  keyField: 'contact_wid',
  shortKey: 'wid',
  route: '/widget',
  connectRoute: '/widget/conexoes',
  filterParam: 'widget_id',
  Icon: Globe,
  color: '#00a884',
  service: widgetService,
  loadMessages: (chatId) => loadWidgetMessages(chatId),
  normalizeMediaType: (t) => (t === 'image' ? 'image' : null),
  allowDocument: false,
  allowAudio: false,
  windowed: false,
  hasHandle: false,
  sendMedia: (chat, url, type) => widgetService.sendMedia(chat.id, url, type),
  handoffDismissKey: 'handoffDismissedWD',
  ConnectionModal: null,
  publishStatus: null,
  oauth: null,
  ListExtras: WidgetSelector,
  HeaderExtra: null,
  renderBubbleExtra,
  rowSecondary: null,
  headerStatus: (chat, account) => {
    const w = (account?.widgets || []).find((x) => x.id === chat?.widget_id);
    return w ? `Widget · ${w.name}` : 'Widget do site';
  },
  texts: {
    searchPlaceholder: 'Pesquisar por nome ou e-mail',
    searchHint: 'Tente buscar por outro nome ou e-mail',
    emptyHint: () => 'As conversas aparecem aqui quando um visitante escrever no chat do seu site.',
    placeholderHint: 'A conversa começa pelo visitante do site. O que você enviar chega quando ele estiver com o chat aberto.',
    noAccount: 'Nenhum widget instalado no site',
    connectButton: 'Criar widget',
    closedNotice: () => '',
    mediaRejected: 'O widget só aceita imagem. Outros anexos foram ignorados.',
  },
};

export default widgetAdapter;
